import { htmlToText } from './util';

// HTML-based message-content helpers (formerly backed by draft-js EditorState).
// The message content is now represented as an HTML string throughout the app;
// the live editor selection/undo lives in the TipTap instance held by the
// component, not in these helpers. Functions that historically operated on a
// draft-js EditorState now operate on / return HTML strings.
export const DraftHelpers = {
  // Input may be text or html. Returns normalized HTML.
  setContents: (html) => {
    if (typeof html !== 'string') {
      throw new Error('DraftHelpers.setContents invoked with missing parameters');
    }
    html = DraftHelpers.textToDraftHtml(html);
    html = DraftHelpers.fixDraftFormatting(html);

    return html;
  },

  // Appends plain text to the current HTML. At this layer there is no live
  // selection (the TipTap instance owns the cursor), so callers that need
  // at-cursor insertion use the editor command directly; this remains an
  // append for thunk/store-level callers.
  replaceCurrentSelection: (text, currentHtml = '') => {
    if (typeof text !== 'string') {
      throw new Error('DraftHelpers.replaceCurrentSelection invoked with missing parameters');
    }

    return DraftHelpers.appendHtmlToContents(text, currentHtml || '');
  },

  // Only pure text is accepted for textToAdd. Operates on the current HTML string.
  replaceRegularExpression: (textToAdd, currentHtml = '', regEx, removeSpaces) => {
    if (typeof textToAdd !== 'string') {
      throw new Error('DraftHelpers.replaceRegularExpression invoked with missing parameters');
    }
    let newText = currentHtml || '';
    if (removeSpaces) {
      // removes line breaks - not needed when inserting text, only removing
      newText = newText.replace(`<br><br>${regEx}`, textToAdd);
    }
    newText = newText.replace(regEx, textToAdd);

    return newText;
  },

  // Input may be text or html. Appends to the current HTML.
  appendHtmlToContents: (html, currentHtml = '') => {
    if (typeof html !== 'string') {
      throw new Error('DraftHelpers.appendHtmlToContents invoked with missing parameters');
    }
    const current = currentHtml || '';
    html = DraftHelpers.textToDraftHtml(html);
    if (current.length === 0 && html.length === 0) {
      return '';
    }

    return DraftHelpers.setContents(current + html);
  },

  // Content is already HTML; normalize it. Accepts an HTML string.
  contentToHtml: (html) => {
    if (!html) {
      return '';
    }

    return DraftHelpers.fixDraftFormatting(html);
  },

  // Plain-text projection of the message HTML (replaces
  // editorState.getCurrentContent().getPlainText()).
  getPlainText: (html) => htmlToText(html || ''),

  // Fixes malformed macros: wraps every text with p tags, but keeps img tags outside of them.
  // Designed specifically for fixing decision tree macros that do not have p tags around text but do have img tags.
  // Also, removes externeous vertical spaces created with newline characters.
  textToDraftHtml: (html) => {
    html = html || '';
    html = html.trim();
    if (htmlToText(html).length !== 0 && (!html.match(/^<p>/) || !html.match(/<\/p>$/))) {
      html = html.replace(/<img(\s+.*?)src.+?>/gi, (match) => {
        return '</p>' + match + '<p>';
      });
      if (html.indexOf('<p>') !== 0) {
        html = '<p>' + html + '</p>';
      }
      html = html.replace(/\n\n<\/p>/g, '</p>');
    }

    return html;
  },

  // Normalizes whitespace/newlines between adjacent p and img elements and
  // puts an html empty space between empty p tags (kept from the draft-js era
  // because macros and stored content still rely on this shape).
  fixDraftFormatting: (html) => {
    html = html || '';
    html = html.replace(/(<\/p>|<img.*?\/>)(\s+)(<p>|<img.*?\/>)/g, (match, g1, g2, g3) => {
      return g1 + g3;
    });
    html = html.replace(/<p><\/p>/g, '<p>&nbsp;</p>').trim();

    return html;
  },

  marginTop: '1em',
  marginBottom: '1em',
};
